import React from "react";
import { FOOTER } from "@/constants/testIds";
import { ALMEDA_LOGO, ALMEDA_URL, BRANDS } from "@/data/brands";
import { HuplaLogo } from "@/components/site/Header";
import { useT } from "@/i18n/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Footer() {
  const { t } = useT();
  const navLinks = [
    { id: "hero", l: t("footer.linkHome") },
    { id: "about", l: t("footer.linkAbout") },
    { id: "brands", l: t("footer.linkBrands") },
    { id: "sustainability", l: t("footer.linkSustainability") },
    { id: "b2b", l: t("footer.linkB2B") },
    { id: "contact", l: t("footer.linkContact") },
  ];
  return (
    <footer
      id="footer"
      data-testid={FOOTER.root}
      className="relative bg-[#001F39] text-white pt-24 pb-10 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-32 left-1/3 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
           style={{ background: "radial-gradient(circle, #25bfcf 0%, transparent 60%)" }} />
      <img
        aria-hidden
        src="/hupla-emblem.svg"
        alt=""
        className="pointer-events-none select-none absolute -right-40 top-1/2 -translate-y-1/2 w-[720px] max-w-none opacity-[0.06]"
        style={{ filter: "brightness(0) invert(1)" }}
      />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="border-b border-white/10 pb-16">
          <p className="overline text-[#25bfcf]">{t("footer.overline")}</p>
          <h3 className="font-display font-black mt-6 leading-[0.95] text-[clamp(2.5rem,7vw,6rem)]">
            <span className="font-editorial italic text-[#25bfcf]">
              {t("footer.titleJoy")}
            </span>{" "}
            {t("footer.titleRest")}
          </h3>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 py-16">
          <div className="lg:col-span-4">
            <div
              className="p-4 rounded-2xl inline-block"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <HuplaLogo className="h-16" wordmarkColor="#ffffff" />
            </div>
            <p className="mt-6 text-white/70 leading-relaxed max-w-sm">
              {t("footer.desc")}
            </p>
          </div>

          <div className="lg:col-span-3">
            <p className="overline text-white/50">{t("footer.navigate")}</p>
            <ul className="mt-5 space-y-3">
              {navLinks.map((i) => (
                <li key={i.id}>
                  <button
                    onClick={() => scrollTo(i.id)}
                    className="text-white/80 hover:text-[#25bfcf] transition-colors"
                  >
                    {i.l}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <p className="overline text-white/50">{t("footer.ourBrands")}</p>
            <ul className="mt-5 grid grid-cols-2 gap-y-3 gap-x-4">
              {BRANDS.map((b) => (
                <li key={b.slug}>
                  <button
                    onClick={() => scrollTo(`brand-${b.slug}`)}
                    className="flex items-center gap-2 text-white/80 hover:text-[#25bfcf] transition-colors text-left"
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: b.palette.accent }}
                    />
                    {b.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Credibility strip — verifiable facts, styled to match the Hero marquee */}
        <div
          data-testid="footer-trust-strip"
          className="border-t border-white/10 py-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-white/70"
        >
          {[
            "Est. 1998 · İstanbul",
            "7 house brands · 40+ SKUs",
            "Export-ready",
            "Group of De Almeda Seligmann",
          ].map((label, i, arr) => (
            <React.Fragment key={label}>
              <span
                className="text-[0.7rem] md:text-xs font-bold uppercase"
                style={{ letterSpacing: "0.22em" }}
              >
                {label}
              </span>
              {i < arr.length - 1 && (
                <svg
                  aria-hidden
                  viewBox="0 0 74.27 75.18"
                  className="w-3 h-3 md:w-3.5 md:h-3.5 text-[#25bfcf] flex-shrink-0"
                  fill="currentColor"
                >
                  <path d="M69.09 27.07l-6.28-4.81V13.2C62.8 5.92 56.89 0 49.61 0c-1.49 0-2.96.25-4.37.75l-8.09 2.83L29.06.75C27.64.25 26.17 0 24.69 0a13.12 13.12 0 0 0-9.22 3.76c-2.57 2.5-3.98 5.86-3.98 9.45v9.06L5.18 27.1C1.88 29.62 0 33.44 0 37.59s1.9 7.97 5.2 10.49l6.29 4.8v8.83c0 7.28 5.92 13.2 13.19 13.2 1.49 0 2.96-.25 4.37-.75l8.09-2.83 8.09 2.83c1.41.49 2.88.75 4.37.75 7.28 0 13.19-5.92 13.19-13.2v-8.94l6.19-4.65a13.1 13.1 0 0 0 5.27-10.49 13.08 13.08 0 0 0-5.18-10.54zm-5.62 13.68l-7.27 5.46c-1.63 1.23-2.6 3.15-2.6 5.2V61.7c0 2.29-1.87 4-4 4a3.92 3.92 0 0 1-1.33-.23l-8.98-3.15a6.61 6.61 0 0 0-2.15-.37c-.73 0-1.45.12-2.15.37l-8.98 3.15a3.88 3.88 0 0 1-1.33.23c-2.12 0-4-1.72-4-4V51.54a6.51 6.51 0 0 0-2.56-5.17l-7.36-5.61a4 4 0 0 1 0-6.36l7.37-5.64c1.61-1.23 2.55-3.14 2.55-5.16V13.21c0-2.29 1.87-4 4-4a3.92 3.92 0 0 1 1.33.23l8.98 3.15a6.61 6.61 0 0 0 2.15.37c.73 0 1.45-.12 2.15-.37l8.98-3.15a3.88 3.88 0 0 1 1.33-.23c2.12 0 4 1.72 4 4V23.6c0 2.02.94 3.93 2.55 5.16l7.34 5.62a4.01 4.01 0 0 1-.03 6.38z" />
                </svg>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Group company block — clickable, links to the parent holding site */}
        <div className="border-t border-white/10 pt-10 pb-10 grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-8">
            <p className="overline text-white/50 mb-5">{t("footer.groupCompany")}</p>
            <a
              href={ALMEDA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-almeda-link"
              className="group inline-flex flex-col sm:flex-row items-start sm:items-center gap-6 rounded-2xl -m-3 p-3 hover:bg-white/[0.03] transition-colors"
              aria-label="Visit De Almeda Seligmann Capital AG — a Swiss investment holding"
            >
              <img
                data-testid={FOOTER.almedaLogo}
                src={ALMEDA_LOGO}
                alt="De Almeda Seligmann Capital AG"
                className="h-20 w-auto object-contain drop-shadow-[0_6px_18px_rgba(228,0,78,0.25)] transition-transform group-hover:scale-[1.03]"
              />
              <div>
                <p className="text-2xl font-display font-black text-white leading-tight">
                  De Almeda Seligmann
                  <br />
                  Capital AG
                </p>
                <p className="text-sm text-white/60 mt-2 max-w-md">
                  {t("footer.groupTagline")}
                </p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#25bfcf] group-hover:text-white transition-colors">
                  www.dealmedaseligmann.ch
                  <span aria-hidden className="transition-transform group-hover:translate-x-0.5">↗</span>
                </span>
              </div>
            </a>
          </div>
          <div className="lg:col-span-4 text-sm text-white/60 leading-relaxed">
            <p className="overline text-white/40 mb-2">{t("footer.headOffice")}</p>
            <p>
              Büyükdere Cd. 175/6, Ferko Signature
              <br />
              Esentepe, Şişli 34394 İstanbul, Türkiye
            </p>
            <p className="mt-3">
              +90 850 302 40 60 &nbsp;·&nbsp; +90 532 284 42 83
            </p>
            <p className="mt-2">
              <a href="mailto:hello@hupla.world" className="hover:text-[#25bfcf]">
                hello@hupla.world
              </a>
            </p>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-wrap items-center justify-between gap-4 text-sm text-white/50">
          <p>
            © {new Date().getFullYear()} Hupla Gıda Üretim A.Ş. {t("footer.copyright")}
          </p>
          <p>{t("footer.crafted")}</p>
        </div>
      </div>
    </footer>
  );
}
